import * as React from "react";
import { useParams } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, Badge, Button, Skeleton, useChat } from "randmarcomps";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { AlertTriangle, ClipboardList, FileText, ReceiptText, Sparkles } from "lucide-react";
import { useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery } from "randmar-api-client";
import { IPurchaseOrderResult, IReceiptResult, IRequisitionResult } from "../api/document/DocumentsApi";
import PurchaseOrderView from "../components/document/PurchaseOrderView";
import ReceiptView from "../components/document/ReceiptView";
import RequisitionView from "../components/document/RequisitionView";
import DocumentActionsMenu from "../components/document/DocumentActionsMenu";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import { useManufacturer } from "../contexts/WithManufacturer";
import { PurchaseOrder, Receipt, Requisition } from "../stores/document/Document";

const handleDocumentName = (docNum: string) => {
  if (docNum.startsWith("PO") && docNum.length === 9) return "Purchase Order";
  if (docNum.startsWith("RQ") && docNum.length === 9) return "Draft Purchase";
  if (docNum.startsWith("RC") && docNum.length === 9) return "Reception";
  return "Document";
};

type DocumentKind = "purchase-order" | "draft-purchase" | "reception" | "unknown";

const getDocumentKind = (docNum: string): DocumentKind => {
  if (docNum.startsWith("PO") && docNum.length === 9) return "purchase-order";
  if (docNum.startsWith("RQ") && docNum.length === 9) return "draft-purchase";
  if (docNum.startsWith("RC") && docNum.length === 9) return "reception";
  return "unknown";
};

function buildDocumentAssistantPrompt(options: {
  applicationId: string;
  documentNumber: string;
  documentName: string;
  kind: DocumentKind;
  purchaseOrder?: PurchaseOrder | null;
  requisition?: Requisition | null;
  receipt?: Receipt | null;
}) {
  const lines: string[] = [
    "You are assisting with the Randmar Manufacturer Portal Document page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    "",
    `Document: ${options.documentName} (${options.documentNumber})`,
    `Kind: ${options.kind}`,
    "",
  ];

  if (options.kind === "purchase-order" && options.purchaseOrder) {
    const po = options.purchaseOrder;
    lines.push(
      `Vendor: ${po.vendorName ?? "Unknown"}`,
      `Location: ${po.location ?? "Unknown"}`,
      `PurchaseOrderDate: ${po.purchaseOrderDate ?? "Unknown"}`,
      `ExpectedArrivalDate: ${po.expectedArrivalDate ?? "Unknown"}`,
      `Subtotal: ${po.subTotal ?? 0}`,
      `Line items: ${(po.partNumbers ?? []).length}`,
      `Receipts: ${(po.receipts ?? []).length}`,
      ""
    );
  }

  if (options.kind === "draft-purchase" && options.requisition) {
    const rq = options.requisition;
    lines.push(
      `RequestedBy: ${rq.requestedBy ?? "Unknown"}`,
      `Reference: ${rq.reference ?? "None"}`,
      `Location: ${rq.location ?? "Unknown"}`,
      `DraftDate: ${rq.requisitionDate ?? "Unknown"}`,
      `Subtotal: ${rq.subTotal ?? 0}`,
      `Line items: ${(rq.partNumbers ?? []).length}`,
      ""
    );
  }

  if (options.kind === "reception" && options.receipt) {
    const rc = options.receipt;
    lines.push(
      `PurchaseOrderNumber: ${rc.purchaseOrderNumber ?? "Unknown"}`,
      `Location: ${rc.location ?? "Unknown"}`,
      `ReceiptDate: ${rc.receiptDate ?? "Unknown"}`,
      `Invoiced: ${rc.invoiced ?? "Unknown"}`,
      `InvoiceNumber: ${rc.invoiceNumber ?? "None"}`,
      `Total: ${rc.total ?? 0}`,
      `Line items: ${(rc.partNumbers ?? []).length}`,
      ""
    );
  }

  lines.push(
    "Help me understand what this document says, flag anything unusual, and suggest next actions I should take."
  );
  return lines.join("\n");
}

const Document: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { documentNumber = "" } = useParams<{ documentNumber: string }>();
  const documentName = handleDocumentName(documentNumber);
  const documentKind = React.useMemo(() => getDocumentKind(documentNumber), [documentNumber]);
  const { sendMessageWithClear } = useChat();

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const canQuery = Boolean(applicationId && documentNumber);
  const documentQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberQuery(
    canQuery ? { routeManufacturerId: applicationId, documentNumber } : skipToken
  );

  const documentData = documentQuery.data;
  const isLoading = documentQuery.isLoading;
  const isRefreshing = documentQuery.isFetching;

  React.useEffect(() => {
    const previousTitle = window.document.title;
    if (documentNumber) {
      window.document.title = `${documentName} ${documentNumber} | ${previousTitle}`;
    } else {
      window.document.title = `${documentName} | ${previousTitle}`;
    }
    return () => {
      window.document.title = previousTitle;
    };
  }, [documentName, documentNumber]);

  React.useEffect(() => {
    if (!canQuery) {
      setLastRefreshedAt(null);
      return;
    }
    if (!isRefreshing && (documentQuery.isSuccess || documentQuery.isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [canQuery, documentQuery.isError, documentQuery.isSuccess, isRefreshing]);

  const purchaseOrder = React.useMemo(() => {
    if (!documentData || documentKind !== "purchase-order") return null;
    return PurchaseOrder.convertFrom(documentData as IPurchaseOrderResult);
  }, [documentData, documentKind]);

  const requisition = React.useMemo(() => {
    if (!documentData || documentKind !== "draft-purchase") return null;
    return Requisition.convertFrom(documentData as IRequisitionResult);
  }, [documentData, documentKind]);

  const receipt = React.useMemo(() => {
    if (!documentData || documentKind !== "reception") return null;
    return Receipt.convertFrom(documentData as IReceiptResult);
  }, [documentData, documentKind]);

  const assistantPrompt = React.useMemo(() => {
    if (!applicationId) {
      return "";
    }
    return buildDocumentAssistantPrompt({
      applicationId,
      documentNumber,
      documentName,
      kind: documentKind,
      purchaseOrder,
      requisition,
      receipt,
    });
  }, [applicationId, documentKind, documentName, documentNumber, purchaseOrder, receipt, requisition]);

  const documentBadge = React.useMemo(() => {
    switch (documentKind) {
      case "purchase-order":
        return { label: "Purchase order", Icon: FileText, variant: "success" as const };
      case "draft-purchase":
        return { label: "Draft purchase", Icon: ClipboardList, variant: "warning" as const };
      case "reception":
        return { label: "Reception", Icon: ReceiptText, variant: "info" as const };
      default:
        return { label: "Document", Icon: FileText, variant: "secondary" as const };
    }
  }, [documentKind]);

  if (isLoading) {
    return (
      <main className="mx-auto w-full max-w-[1440px] space-y-6 pb-10">
        <header className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3">
              <Skeleton className="h-6 w-44 rounded-xl" />
              <Skeleton className="h-10 w-72 rounded-xl" />
              <Skeleton className="h-5 w-40 rounded-xl" />
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Skeleton className="h-10 w-72 rounded-xl" />
              <Skeleton className="h-10 w-28 rounded-xl" />
            </div>
          </div>
        </header>
        <Skeleton className="h-[520px] w-full rounded-2xl border border-gray-100 bg-white shadow-sm" />
      </main>
    );
  }

  if (!documentData) {
    return (
      <main className="mx-auto w-full max-w-[1440px] space-y-6 pb-10">
        <header className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Documents
              </span>
              <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Document</h1>
              <p className="text-[15px] text-slate-600">We couldn’t load this document. Refresh to try again.</p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <DataRefreshBar
                align="right"
                lastRefreshedAt={lastRefreshedAt}
                isRefreshing={isRefreshing}
                onRefresh={canQuery ? () => documentQuery.refetch() : undefined}
                actions={(
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      if (!applicationId) return;
                      sendMessageWithClear(
                        buildDocumentAssistantPrompt({
                          applicationId,
                          documentNumber,
                          documentName,
                          kind: documentKind,
                        })
                      );
                    }}
                    className="rounded-xl"
                    aria-label="Open assistant with this page context"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              />
            </div>
          </div>
        </header>
        <Alert className="rounded-2xl border border-rose-100 bg-rose-50 shadow-sm">
          <AlertTriangle className="h-4 w-4 text-rose-600" aria-hidden="true" />
          <AlertTitle className="text-slate-900">Document not found</AlertTitle>
          <AlertDescription className="text-slate-700">
            The document you are looking for does not exist or you do not have permission to view it.
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  const documentView = purchaseOrder ? (
    <PurchaseOrderView purchaseOrder={purchaseOrder} />
  ) : requisition ? (
    <RequisitionView requisition={requisition} />
  ) : receipt ? (
    <ReceiptView receipt={receipt} />
  ) : (
    <Alert className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
      <AlertTitle className="text-slate-900">Unsupported document type</AlertTitle>
      <AlertDescription className="text-slate-700">
        This document number format is not yet supported in the new Document view.
      </AlertDescription>
    </Alert>
  );

  return (
    <main className="mx-auto w-full max-w-[1440px] space-y-8 pb-10" aria-busy={isRefreshing}>
      <header className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <documentBadge.Icon className="h-4 w-4" aria-hidden="true" />
                Documents
              </span>
              <Badge variant={documentBadge.variant} size="sm">
                {documentBadge.label}
              </Badge>
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">{documentName}</h1>
            <p className="text-[15px] font-mono text-slate-500">{documentNumber}</p>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isRefreshing}
              onRefresh={canQuery ? () => documentQuery.refetch() : undefined}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />
            <DocumentActionsMenu documentNumber={documentNumber} documentName={documentName} />
          </div>
        </div>
      </header>
      {documentView}
    </main>
  );
};

export default Document;
